/*
 * Firebase, fetched when somebody actually signs in.
 *
 * The compat SDK is around 90KB gzipped and it was imported by main.js, so it
 * was downloaded, parsed and initialised on every page load of every screen -
 * for four operations that happen at most once a session: signing in, signing
 * up, signing out, and creating a school account.
 *
 * The one thing that looked like it needed Firebase at boot was the router's
 * isUserLoggedIn(), which read firebase.auth().currentUser into a variable and
 * then returned a localStorage check instead, ignoring it entirely.
 *
 * This module now exports a loader rather than running on import. Everything
 * that needs Firebase awaits it, and the login screen starts the download while
 * the admin is still typing, so the button press does not wait for the network.
 */

let firebasePromise = null;

/**
 * The initialised firebase namespace, with auth attached.
 *
 * Safe to call repeatedly and from anywhere: the download happens once and
 * every later call gets the same instance.
 *
 * @returns {Promise<object>} the firebase compat namespace
 */
export function loadFirebase() {
  if (firebasePromise) {
    return firebasePromise;
  }

  firebasePromise = Promise.all([
    import(/* webpackChunkName: "firebase" */ "firebase/compat/app"),
    import(/* webpackChunkName: "firebase" */ "firebase/compat/auth"),
  ]).then(([appModule]) => {
    const firebase = appModule.default || appModule;

    //initializeApp throws if it is called twice for the default app, and a hot
    //reload or a second caller landing here mid-download would do exactly that
    if (!firebase.apps.length) {
      firebase.initializeApp({
        apiKey: process.env.VUE_APP_API_KEY,
        authDomain: process.env.VUE_APP_AUTH_DOMAIN,
        projectId: process.env.VUE_APP_PROJECT_ID,
        storageBucket: process.env.VUE_APP_STORAGE_BUCKET,
        messagingSenderId: process.env.VUE_APP_MESSAGING_SENDER_ID,
        appId: process.env.VUE_APP_APP_ID,
      });
    }

    return firebase;
  });

  return firebasePromise;
}

/** The auth instance, for the four places that sign somebody in or out. */
export function firebaseAuth() {
  return loadFirebase().then((firebase) => firebase.auth());
}

export default loadFirebase;
